import React, { useEffect, useState } from "react";
import { api } from "../../../config/config";
import { Link, useNavigate } from "react-router-dom";
import SonabelLogo from "../../../assets/Sonabel_logo.svg.jpg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { UICard } from "../ui/UICard";
import { UIButton } from "../ui/UIButton";
import { UIProgressBar } from "../ui/UIProgressBar";
import { UIAlert } from "../ui/UIAlert";
import { UITextInput } from "../ui/UITextInput";
import { UILabel } from "../ui/UILabel";
import { UISpinner } from "../ui/UISpinner";
import { UIRadio } from "../ui/UIRadio";
import { MuiTelInput } from "mui-tel-input";

// --- Définitions et Expressions Régulières ---
const PWD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,24}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_LIMIT = 3;
const COOLDOWN_TIME = 15 * 60 * 1000; // 15 minutes en ms

// --- COMPOSANT PRINCIPAL ---
export default function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [contactMethod, setContactMethod] = useState("email");
  // Désactiver le bouton de renvoi du code après 3 tentatives
  // si la mehode de contact est phone
  const [resendAttempts, setResendAttempts] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(null); // timestamp quand le cooldown se termine
  const [timer, setTimer] = useState(0); // secondes restantes

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+226");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // Pour les erreurs générales (API)
  const [errors, setErrors] = useState({}); // Pour les erreurs de validation par champ
  const [success, setSuccess] = useState("");
  const [isResendingCode, setIsResendingCode] = useState(false);

  // Charger l'état depuis localStorage
  useEffect(() => {
    const storedEnd = localStorage.getItem("resendCooldownEnd");
    const storedAttempts = localStorage.getItem("resendAttempts");

    if (storedAttempts) setResendAttempts(Number(storedAttempts));
    if (storedEnd) {
      const end = Number(storedEnd);
      const now = Date.now();
      if (end > now) {
        setCooldownEnd(end);
        setTimer(Math.ceil((end - now) / 1000));
      }
    }
  }, []);

  // Timer qui se met à jour chaque seconde
  useEffect(() => {
    if (!cooldownEnd) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.ceil((cooldownEnd - now) / 1000);
      if (remaining <= 0) {
        setResendAttempts(0);
        setCooldownEnd(null);
        setTimer(0);
        localStorage.removeItem("resendCooldownEnd");
        localStorage.removeItem("resendAttempts");
        clearInterval(interval);
      } else {
        setTimer(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownEnd]);

  const validateEmail = (email) => EMAIL_REGEX.test(email);
  const validatePhone = (phone) => phone.replace(/\s+/g, "").length >= 8;
  const validatePassword = (password) => PWD_REGEX.test(password);

  const clearMessages = () => {
    setError("");
    setSuccess("");
    setErrors({});
  };

  const handleError = (err, step) => {
    let message;
    if (step === 1) {
      /*   if (!err?.response) {
        message = "Pas de réponse du serveur";
      } else if (err.response.status === 401) {
        message = "Ce compte n'existe pas.";
      } else {
        message = "Une erreur est survenue. Veuillez réessayer.";
      } */
      message =
        "Impossible d'envoyer le message sur le numéro de téléphone fournis";
    } else if (step === 2) {
      /*   if (!err?.response) {
        message = "Pas de réponse du serveur";
      } else if (err.response.status === 401) {
        message = "Code invalide ou expiré.";
      } else {
        message = "La vérification du code a échoué. Veuillez réessayer.";
      } */
      message = "Le code fourni est incorrect ou expiré.";
    } else if (step === 3) {
      /* if (!err?.response) {
        message = "Pas de réponse du serveur";
      } else if (err.response.status === 401) {
        message = "Code invalide ou expiré.";
      } else {
        message = "Le changement de mot de passe a échoué. Veuillez réessayer.";
      } */
      message =
        " Une erreur est survenue lors de la réinitialisation du mot de passe";
    }

    setError(message);
  };

  const handleSendCode = async () => {
    const newErrors = {};
    clearMessages();

    if (contactMethod === "email" && !validateEmail(email)) {
      //setError("Veuillez saisir une adresse email valide.");
      newErrors.email = "Veuillez saisir une adresse email valide.";
      //return;
    }
    if (contactMethod === "phoneNumber" && !validatePhone(phone)) {
      //setError("Veuillez saisir un numéro de téléphone valide.");
      newErrors.phone = "Veuillez saisir un numéro de téléphone valide.";
      //return;
    }

    // Si des erreurs de validation sont présentes, on les affiche et on arrête le processus
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    let payload = contactMethod === "email" ? email : phone;
    // Si c'est un numéro de téléphone → supprimer les espaces
    if (contactMethod === "phoneNumber") {
      payload = payload.replace(/\s+/g, "");
    }

    const data = {
      step: "first",
    };
    // Changer la clé 'contactMethod' en fonction de la méthode choisie
    data[contactMethod] = payload;

    await api
      .post("account/lost-password", data)
      .then((response) => {
        if (response.status === 200) {
          setSuccess(
            `Code envoyé à ${contactMethod === "email" ? email : phone}.`
          );
          setTimeout(() => {
            setStep(2);
            setSuccess("");
          }, 2000);
        }
      })
      .catch((error) => {
        handleError(error, 1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleVerifyCode = async () => {
    clearMessages();
    setLoading(true);
    let payload = contactMethod === "email" ? email : phone;
    // Si c'est un numéro de téléphone → supprimer les espaces
    if (contactMethod === "phoneNumber") {
      payload = payload.replace(/\s+/g, "");
    }

    const data = {
      step: "second",
      code: code,
    };
    // Changer la clé 'contactMethod' en fonction de la méthode choisie
    data[contactMethod] = payload;
    await api
      .post("account/lost-password", data)
      .then((response) => {
        if (response.status === 200) {
          setSuccess("Code vérifié avec succès");
          setTimeout(() => {
            setStep(3);
            setSuccess("");
          }, 2000);
        }
      })
      .catch((error) => {
        handleError(error, 2);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangePassword = async () => {
    clearMessages();
    const newErrors = {};

    if (!validatePassword(newPassword)) {
      newErrors.newPassword =
        "Le mot de passe doit contenir 8 caratères au minimun. Doit comprendre des lettres majuscules et minuscules, un chiffre et un caractère spécial.";
    }
    if (newPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    let payload = contactMethod === "email" ? email : phone;
    // Si c'est un numéro de téléphone → supprimer les espaces
    if (contactMethod === "phoneNumber") {
      payload = payload.replace(/\s+/g, "");
    }

    const data = {
      step: "last",
      code: code,
      password: newPassword,
    };

    // Changer la clé 'contactMethod' en fonction de la méthode choisie
    data[contactMethod] = payload;

    await api
      .post("account/lost-password", data)
      .then((response) => {
        if (response.status === 200) {
          setSuccess(
            "Mot de passe changé avec succès ! Vous allez être redirigé."
          );
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }
      })
      .catch((error) => {
        handleError(error, 3);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResendCode = async () => {
    clearMessages();
    // Empêcher de dépasser 3 tentatives
    if (contactMethod === "phoneNumber" && resendAttempts >= RESEND_LIMIT) {
      return; // limite atteinte
    }
    setIsResendingCode(true);
    setLoading(true);

    let payload = contactMethod === "email" ? email : phone;

    // Si c'est un numéro de téléphone → supprimer les espaces
    if (contactMethod === "phoneNumber") {
      payload = payload.replace(/\s+/g, "");
    }

    const data = {
      step: "first",
    };

    // Changer la clé 'contactMethod' en fonction de la méthode choisie
    data[contactMethod] = payload;
    await api
      .post("account/lost-password", data)
      .then((response) => {
        if (response.status === 200) {
          setSuccess(
            `Nouveau code envoyé à ${
              contactMethod === "email" ? email : phone
            }.`
          );
          // Incrémenter seulement si c’est par SMS
          /*   if (contactMethod === "phoneNumber") {
            setResendAttempts((prev) => {
              const newAttempts = prev + 1;

              if (newAttempts >= 3) {
                // Réinitialisation après 15 min (900000 ms)
                setTimeout(() => {
                  setResendAttempts(0);
                }, 15 * 60 * 1000);
              }

              return newAttempts;
            });
          } */
          if (contactMethod === "phoneNumber") {
            const newAttempts = resendAttempts + 1;
            setResendAttempts(newAttempts);
            localStorage.setItem("resendAttempts", newAttempts);

            if (newAttempts >= RESEND_LIMIT) {
              const endTime = Date.now() + COOLDOWN_TIME;
              setCooldownEnd(endTime);
              localStorage.setItem("resendCooldownEnd", endTime);
            }
          }
          setTimeout(() => {
            setStep(2);
            setSuccess("");
          }, 2000);
        }
      })
      .catch((error) => {
        handleError(error, 1);
      })
      .finally(() => {
        setLoading(false);
        setIsResendingCode(false);
      });
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    clearMessages();
  };

  const Logo = () => (
    <img src={SonabelLogo} alt="Logo de l'entreprise" className="mr-4 h-10" />
  );

  const LogoSection = () => (
    <Link
      to="/"
      className="flex items-center justify-center mb-8 text-2xl font-semibold"
    >
      <Logo />
      <span>SONABEL</span>
    </Link>
  );

  const renderStep1 = () => (
    <>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Mot de passe oublié
        </h1>
        <p className="text-gray-600">
          Saisissez votre email ou numéro de téléphone pour recevoir un code.
        </p>
      </div>
      <div className="space-y-6">
        <div>
          <UILabel>Comment recevoir le code ?</UILabel>
          <div className="flex space-x-6 mt-2">
            <div className="flex items-center">
              <UIRadio
                id="email-option"
                name="contact-method"
                value="email"
                checked={contactMethod === "email"}
                onChange={(e) => setContactMethod(e.target.value)}
                disabled={loading}
              />
              <UILabel htmlFor="email-option" className="ml-2 mb-0">
                Par email
              </UILabel>
            </div>
            <div className="flex items-center">
              <UIRadio
                id="phone-option"
                name="contact-method"
                value="phoneNumber"
                checked={contactMethod === "phoneNumber"}
                onChange={(e) => setContactMethod(e.target.value)}
                disabled={loading}
              />
              <UILabel htmlFor="phone-option" className="ml-2 mb-0">
                Par SMS
              </UILabel>
            </div>
          </div>
        </div>
        {contactMethod === "email" ? (
          <div>
            <UILabel htmlFor="email">Adresse email</UILabel>
            <UITextInput
              id="email"
              type="email"
              placeholder="exemple@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete={"email"}
              className={`pr-10 ${
                errors.email
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : ""
              }`}
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        ) : (
          <div>
            <UILabel htmlFor="phone">Numéro de téléphone</UILabel>
            <MuiTelInput
              id="phone"
              fullWidth
              size="small"
              value={phone}
              onChange={(value) => setPhone(value)}
              placeholder="+226 25 31 37 00"
              required
              autoComplete="tel"
              disabled={loading}
              sx={{
                backgroundColor: "rgb(249 250 251)", // bg-gray-50
                borderColor: "rgb(209 213 219)", // border-gray-300
                color: "rgb(17 24 39)", // text-gray-900
                fontSize: "0.875rem", // text-sm
                borderRadius: "0.5rem", // rounded-lg
                // Ciblez la racine de l'OutlinedInput pour les styles de bordure/focus
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    // Ciblez la bordure (fieldset) de l'input
                    borderColor: "rgb(209 213 219)",
                  },
                  "&:hover fieldset": {
                    // État survol pour la bordure
                    borderColor: "rgb(209 213 219)",
                  },
                  "&.Mui-focused fieldset": {
                    // État de focus pour la bordure
                    borderColor: "rgb(14, 159, 10)", // focus:border-green-500 (ajustez à votre vert-500)
                    boxShadow: "0 0 0 0px rgb(14, 159, 10)", // focus:ring-green-500 (ajustez)
                  },
                },
                // Ciblez l'élément input réel pour le padding

                "& .MuiInputBase-input": {
                  padding: "0.625rem 0.75rem",
                  // p-2.5 (ajustez si nécessaire, MUI a un padding par défaut)
                },
                // Styles pour l'état désactivé
                "&.Mui-disabled": {
                  backgroundColor: "rgb(229 231 235)", // disabled:bg-gray-200
                  color: "rgb(107 114 128)", // disabled:text-gray-500
                },
                display: "block", // block
                width: "100%", // w-full
                // Supprimez aussi l'outline sur le composant racine
              }}
            />
          </div>
        )}
        {/*   <UIButton
          onClick={handleSendCode}
          disabled={loading || (contactMethod === "email" ? !email : !phone)}
          variant="primary"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <UISpinner className="mr-2" /> Envoi en cours...
            </div>
          ) : (
            "Envoyer le code"
          )}
        </UIButton> */}
        <UIButton
          onClick={handleSendCode}
          disabled={
            loading ||
            (contactMethod === "email" ? !email : !phone) ||
            (contactMethod === "phoneNumber" && cooldownEnd) // désactivé si cooldown actif
          }
          variant="primary"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <UISpinner className="mr-2" /> Envoi en cours...
            </div>
          ) : contactMethod === "phoneNumber" && cooldownEnd ? (
            `Réessayez dans ${Math.floor(timer / 60)
              .toString()
              .padStart(2, "0")}:${(timer % 60).toString().padStart(2, "0")}`
          ) : (
            "Envoyer le code"
          )}
        </UIButton>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Saisissez votre code
        </h1>
        <p className="text-gray-600">
          Saisissez le code envoyé par{" "}
          {contactMethod === "email" ? "email" : "SMS"}.
        </p>
      </div>
      <div className="space-y-6">
        <div>
          <UILabel htmlFor="code">Code de vérification</UILabel>
          <UITextInput
            id="code"
            type="text"
            placeholder="Votre code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
            disabled={loading}
            autoComplete={"one-time-code"}
            className="text-center text-lg tracking-wider"
          />
        </div>
        <UIButton
          onClick={handleVerifyCode}
          disabled={loading || !code}
          variant="primary"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <UISpinner className="mr-2" />{" "}
              {isResendingCode ? "Envoi en cours..." : "Vérification..."}
            </div>
          ) : (
            "Soumettre le code"
          )}
        </UIButton>
        <div className="flex space-x-3">
          <UIButton
            onClick={handleResendCode}
            disabled={
              loading ||
              (contactMethod === "phoneNumber" &&
                resendAttempts >= RESEND_LIMIT)
            }
            variant="outline"
            className="flex-1"
          >
            {contactMethod === "phoneNumber" && resendAttempts >= RESEND_LIMIT
              ? `Réessayez dans ${Math.floor(timer / 60)
                  .toString()
                  .padStart(2, "0")}:${(timer % 60)
                  .toString()
                  .padStart(2, "0")}`
              : "Renvoyer le code"}
          </UIButton>

          <UIButton
            onClick={handleBackToStep1}
            disabled={loading}
            variant="ghost"
            className="flex-1"
          >
            Retour
          </UIButton>
        </div>
        {contactMethod === "phoneNumber" && resendAttempts >= 3 && (
          <p className="text-red-600 text-sm mt-2">
            Vous avez atteint la limite de tentatives par SMS. Réessayez dans 15
            minutes.
          </p>
        )}
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Réinitialisez votre mot de passe
        </h1>
        <p className="text-gray-600">
          Choisissez un nouveau mot de passe sécurisé.
        </p>
      </div>
      <div className="space-y-6">
        <div>
          <UILabel htmlFor="newPassword">Nouveau mot de passe</UILabel>
          <div className="relative">
            <UITextInput
              id="newPassword"
              type={isPasswordVisible ? "text" : "password"}
              placeholder="*********"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              className={`pr-10 ${
                errors.newPassword
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              {isPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-red-600 text-sm mt-1">{errors.newPassword}</p>
          )}
        </div>
        <div>
          <UILabel htmlFor="confirmPassword">Confirmer le mot de passe</UILabel>
          <div className="relative">
            <UITextInput
              id="confirmPassword"
              type={isPasswordVisible ? "text" : "password"}
              placeholder="***********"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              className={`pr-10 ${
                errors.confirmPassword
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              {/*  {isPasswordVisible ? <EyeSlashIcon /> : <EyeIcon />} */}
              {isPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-600 text-sm mt-1">
              {errors.confirmPassword}
            </p>
          )}
        </div>
        <UIButton
          onClick={handleChangePassword}
          disabled={loading || !newPassword || !confirmPassword}
          variant="primary"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <UISpinner className="mr-2" /> Changement en cours...
            </div>
          ) : (
            "Changer le mot de passe"
          )}
        </UIButton>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <LogoSection />
      <UICard className="max-w-md w-full p-8">
        <UIProgressBar currentStep={step} totalSteps={3} />
        {success && (
          <UIAlert color="green" className="mb-6">
            {success}
          </UIAlert>
        )}
        {error && (
          <UIAlert color="red" className="mb-6">
            {error}
          </UIAlert>
        )}
        <form>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </form>
        <div className="mt-8 text-center">
          <UIButton
            onClick={() => navigate("/login")}
            disabled={loading}
            variant="ghost"
            className="text-sm"
          >
            Retour à la connexion
          </UIButton>
        </div>
      </UICard>
    </div>
  );
}
